// src/odoo/helpdesk_dashboard/api/helpdeskDashboardApi.ts
// All comments are in English.

import { http } from "@/core/http";

export interface HelpdeskDashboardFilters {
  startDate?: string;
  endDate?: string;
  assignee?: string;
  salesperson?: string;
  customer?: string;
  stage?: string;
  ticketType?: string;
  tags?: string;
}

export interface HelpdeskSummaryResponse {
  total_tickets: number;
  open_tickets: number;
  solved_tickets: number;
  overdue_tickets: number;
}

export interface MonthlyTrendPoint {
  month: string;
  ticket_count: number;
}

export interface MonthlyTrendTicketInfo {
  ticket_id: number;
  ticket_reference: string;
  subject: string;
  customer: string;
  assigned_to: string;
  salesperson: string;
  stage: string;
  ticket_type: string;
  sub_type: string;
  created_on: string;
  tags: string;
}

export interface HelpdeskTicketDrilldownFilters extends HelpdeskDashboardFilters {
  month?: string;
  subType?: string;
  ageBucket?: string;
  ticketScope?: "all" | "open" | "solved" | "overdue";
}

export interface NamedCountPoint {
  label: string;
  ticket_count: number;
}

export interface AssigneeMonthlyPoint {
  month: string;
  assigned_to: string;
  ticket_count: number;
}

export interface CustomerIssueTypePoint {
  customer: string;
  ticket_type: string;
  ticket_count: number;
}

export interface OpenAgingPoint {
  age_bucket: string;
  assigned_to: string;
  ticket_count: number;
}

const buildParams = (filters: HelpdeskDashboardFilters) => ({
  start_date: filters.startDate,
  end_date: filters.endDate,
  assignee: filters.assignee,
  salesperson: filters.salesperson,
  customer: filters.customer,
  stage: filters.stage,
  ticket_type: filters.ticketType,
  tags: filters.tags,
});

export async function fetchHelpdeskSummary(
  filters: HelpdeskDashboardFilters
): Promise<HelpdeskSummaryResponse> {
  const res = await http.get<HelpdeskSummaryResponse>(
    "/helpdesk-dashboard/summary",
    { params: buildParams(filters) }
  );
  return res.data;
}

export async function fetchHelpdeskMonthlyTrend(
  filters: HelpdeskDashboardFilters
): Promise<MonthlyTrendPoint[]> {
  const res = await http.get<MonthlyTrendPoint[]>(
    "/helpdesk-dashboard/monthly-trend",
    { params: buildParams(filters) }
  );
  return res.data;
}

export async function fetchHelpdeskMonthlyTrendTickets(
  filters: HelpdeskDashboardFilters,
  month: string
): Promise<MonthlyTrendTicketInfo[]> {
  const res = await http.get<MonthlyTrendTicketInfo[]>("/helpdesk-dashboard/tickets", {
    params: { ...buildParams(filters), month },
  });
  return res.data;
}

export async function fetchHelpdeskTickets(
  filters: HelpdeskTicketDrilldownFilters
): Promise<MonthlyTrendTicketInfo[]> {
  const res = await http.get<MonthlyTrendTicketInfo[]>("/helpdesk-dashboard/tickets", {
    params: {
      ...buildParams(filters),
      month: filters.month,
      sub_type: filters.subType,
      age_bucket: filters.ageBucket,
      ticket_scope: filters.ticketScope,
    },
  });
  return res.data;
}

export async function fetchHelpdeskStageDistribution(
  filters: HelpdeskDashboardFilters
): Promise<NamedCountPoint[]> {
  const res = await http.get<NamedCountPoint[]>(
    "/helpdesk-dashboard/stage-distribution",
    { params: buildParams(filters) }
  );
  return res.data;
}

export async function fetchHelpdeskIssueTypes(
  filters: HelpdeskDashboardFilters
): Promise<NamedCountPoint[]> {
  const res = await http.get<NamedCountPoint[]>(
    "/helpdesk-dashboard/issue-types",
    { params: buildParams(filters) }
  );
  return res.data;
}

export async function fetchHelpdeskSubTypes(
  filters: HelpdeskDashboardFilters
): Promise<NamedCountPoint[]> {
  const res = await http.get<NamedCountPoint[]>(
    "/helpdesk-dashboard/sub-types",
    { params: buildParams(filters) }
  );
  return res.data;
}

export async function fetchHelpdeskAssigneesMonthly(
  filters: HelpdeskDashboardFilters
): Promise<AssigneeMonthlyPoint[]> {
  const res = await http.get<AssigneeMonthlyPoint[]>(
    "/helpdesk-dashboard/assignees/monthly",
    { params: buildParams(filters) }
  );
  return res.data;
}

export async function fetchHelpdeskAssigneesOverall(
  filters: HelpdeskDashboardFilters
): Promise<NamedCountPoint[]> {
  const res = await http.get<NamedCountPoint[]>(
    "/helpdesk-dashboard/assignees/overall",
    { params: buildParams(filters) }
  );
  return res.data;
}

export async function fetchHelpdeskSalespersonsOverall(
  filters: HelpdeskDashboardFilters
): Promise<NamedCountPoint[]> {
  const res = await http.get<NamedCountPoint[]>(
    "/helpdesk-dashboard/salespersons/overall",
    { params: buildParams(filters) }
  );
  return res.data;
}

export async function fetchHelpdeskTopProducts(
  filters: HelpdeskDashboardFilters
): Promise<NamedCountPoint[]> {
  const res = await http.get<NamedCountPoint[]>(
    "/helpdesk-dashboard/top-products",
    { params: buildParams(filters) }
  );
  return res.data;
}

export async function fetchHelpdeskTopCustomers(
  filters: HelpdeskDashboardFilters
): Promise<CustomerIssueTypePoint[]> {
  const res = await http.get<CustomerIssueTypePoint[]>(
    "/helpdesk-dashboard/top-customers",
    { params: buildParams(filters) }
  );
  return res.data;
}

export async function fetchHelpdeskOpenAging(
  filters: HelpdeskDashboardFilters
): Promise<OpenAgingPoint[]> {
  const res = await http.get<OpenAgingPoint[]>(
    "/helpdesk-dashboard/open-aging",
    { params: buildParams(filters) }
  );
  return res.data;
}
